import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { api, BRAND_LABELS } from "@/lib/api";
import { waLink, WhatsAppIcon } from "@/lib/whatsapp";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ModelCard from "@/components/ModelCard";
import SpecModal from "@/components/SpecModal";

const CONFIG = {
  honda: {
    brands: ["honda"],
    name: "Honda",
    title: "Honda",
    tag: "Flagship",
    desc: "Our founding brand. Two decades of trust and honest, direct pricing.",
    img: "/cars/showcase-honda.jpg",
    seo: "Honda — AutoWorld",
  },
  proton: {
    brands: ["proton"],
    name: "Proton",
    title: "Proton",
    tag: "Malaysia's National Brand",
    desc: "Malaysia's own, at direct prices. From the legendary Saga to the seven-seater X90.",
    img: "/cars/showcase-proton.jpg",
    seo: "Proton — AutoWorld",
  },
  jaecoo: {
    brands: ["jaecoo", "omoda"],
    name: "Jaecoo & Omoda",
    title: "Jaecoo & Omoda",
    tag: "Newest Addition · Launched 2026",
    desc: "Next-generation luxury SUVs and plug-in hybrids, new to Malaysia. Future-forward mobility, direct from us.",
    img: "/cars/showcase-jaecoo.jpg",
    seo: "Jaecoo Omoda Malaysia — AutoWorld",
  },
};

export default function BrandPage() {
  const { slug } = useParams();
  const cfg = CONFIG[slug];
  const [models, setModels] = useState([]);
  const [offers, setOffers] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!cfg) return;
    document.title = cfg.seo;
    Promise.all(cfg.brands.map((b) => api.models({ brand: b })))
      .then((res) => setModels(res.flat()))
      .catch(() => {});
    Promise.all(cfg.brands.map((b) => api.offers({ brand: b })))
      .then((res) => {
        const seen = new Set();
        setOffers(res.flat().filter((o) => !seen.has(o.id) && seen.add(o.id)));
      })
      .catch(() => {});
  }, [slug]);

  if (!cfg) return <Navigate to="/models" replace />;

  return (
    <div className="pt-16 md:pt-20" data-testid={`brand-page-${slug}`}>
      <section className="relative overflow-hidden border-b border-black/10">
        <img src="/aw-logo.png" alt="" aria-hidden="true" className="pointer-events-none absolute -right-8 top-10 w-[26rem] max-w-[70vw] select-none opacity-[0.05]" />
        <div className="relative mx-auto grid max-w-[1400px] items-center gap-10 px-5 py-14 lg:grid-cols-2 md:px-10 md:py-24">
          <Reveal>
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-neutral-500">
              AutoWorld · {cfg.name}
            </div>
            <h1 className="mt-4 font-display font-extrabold uppercase tracking-tighter" style={{ fontSize: "clamp(2.2rem, 4.6vw, 4.5rem)", lineHeight: 1.02 }}>{cfg.title}</h1>
            <div className="mt-4 inline-block bg-black px-3 py-2 font-mono text-[9px] uppercase tracking-[0.3em] text-white" data-testid="brand-tag">
              {cfg.tag}
            </div>
            <p className="mt-6 max-w-md text-neutral-600 md:text-lg">{cfg.desc}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={waLink(`Hi AutoWorld! I'm interested in a ${cfg.name}. What's your best price?`)}
                target="_blank"
                rel="noreferrer"
                data-testid="brand-whatsapp"
                className="flex items-center gap-2 bg-black px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white hover:bg-neutral-800"
              >
                <WhatsAppIcon className="h-4 w-4" /> Get Best Price
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden border border-black/10">
              <img src={cfg.img} alt={cfg.name} className="img-bw aspect-[16/10] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee items={[`${cfg.name}`, "No Agent · No Markup", "Best Price Guaranteed", "WhatsApp 010-2192686"]} dark={false} />

      <section className="mx-auto max-w-[1400px] px-5 py-16 md:px-10 md:py-24">
        <Reveal className="flex items-end justify-between">
          <h2 className="font-display text-3xl font-extrabold uppercase tracking-tighter md:text-5xl">The Range</h2>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500" data-testid="brand-model-count">
            {models.length} models
          </span>
        </Reveal>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {models.map((m, i) => (
            <ModelCard key={m.id} model={m} index={i} onView={setActive} />
          ))}
        </div>
        {models.length === 0 && (
          <p className="mt-10 border border-black/10 p-12 text-center text-sm text-neutral-500">
            Lineup being updated — WhatsApp us for the latest arrivals.
          </p>
        )}
      </section>

      {offers.length > 0 && (
        <section className="bg-black text-white">
          <div className="mx-auto max-w-[1400px] px-5 py-16 md:px-10 md:py-24">
            <Reveal>
              <h2 className="font-display text-3xl font-extrabold uppercase tracking-tighter md:text-5xl">Current Offers</h2>
            </Reveal>
            <div className="mt-10 grid gap-px border border-white/15 bg-white/15 md:grid-cols-2">
              {offers.map((o) => (
                <div key={o.id} className="bg-black p-8" data-testid={`brand-offer-${o.id}`}>
                  <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-500">
                    {BRAND_LABELS[o.brand] || "All Brands"} · Until {o.valid_until}
                  </div>
                  <div className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight md:text-3xl">{o.highlight}</div>
                  <p className="mt-2 text-sm text-neutral-400">{o.description}</p>
                  <a
                    href={waLink(`Hi AutoWorld! I want to claim: ${o.title} (${o.highlight}).`)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`brand-offer-claim-${o.id}`}
                    className="mt-5 inline-flex items-center gap-2 border-b border-white pb-1 font-mono text-[10px] uppercase tracking-[0.25em] hover:opacity-70"
                  >
                    <WhatsAppIcon className="h-3.5 w-3.5" /> Claim this offer
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <SpecModal model={active} onClose={() => setActive(null)} />
    </div>
  );
}
